<template>
  <b-container fluid class="registration-body">
    <b-row>
      <b-col
        cols="12"
        lg="8"
        order="1"
        order-lg="0"
      >
        <b-form-group>
          <label>{{ $t('asset-information.asset-info.operation-entry') }}
            <span v-if="isTransformer">
              <s-icon
                name="hint"
                v-b-tooltip.hover.right
                :title="$t('asset-information.asset-info.engineering-modules-information')"
              />
            </span>
          </label>
          <s-input-label-edit
            v-model="dateModelEdit"
            :editable="editMode"
            :formattedValue="dateModelRead"
            class="registration-body__entry-input"
          >
            <s-input-date-picker
              v-model="assetInfo.operationEntry"
              :state="operationEntryState"
              :disabled="saving"
              :time-zone="timeZoneUTC"
              @lazy-blur-native-proxy="validateEntryDate"
              @input="clearFieldValidationError('operationEntry')"
            />
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        order="2"
      >
        <b-form-group>
          <label>
            {{ $t('asset-information.asset-info.asset-identification')}}<span v-if="editMode">*
            </span>
          </label>
          <s-input-label-edit
            v-model.trim="assetInfo.identification"
            :editable="editMode"
            :input-options="fieldOptions.identification"
            @input="clearFieldValidationError('identification')"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        order="3"
      >
        <b-form-group>
          <label>{{ $t('asset-information.asset-info.asset-description')}}</label>
          <s-input-label-edit
            v-model.trim="assetInfo.description"
            :editable="editMode"
            :input-options="fieldOptions.description"
            @input="clearFieldValidationError('description')"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        order="4"
      >
        <b-form-group>
          <label>
            {{ $t('asset-information.asset-info.serial-number')}}<span v-if="editMode">*</span>
          </label>
          <s-input-label-edit
            class="text-truncate"
            v-model="assetInfo.serialNumber"
            :editable="editMode"
            :input-options="fieldOptions.serialNumber"
            @input="clearFieldValidationError('serialNumber')"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        order="5"
      >
        <b-form-group>
          <label>{{ $t('asset-information.asset-info.installation-code')}}</label>
          <s-input-label-edit
            class="text-truncate"
            v-model.trim="assetInfo.installationCode"
            :editable="editMode"
            :input-options="fieldOptions.installationCode"
            @input="clearFieldValidationError('installationCode')"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        order="6"
      >
        <b-form-group>
          <label>{{ $t('asset-information.asset-info.operational-code')}}</label>
          <s-input-label-edit
            class="text-truncate"
            v-model.trim="assetInfo.operationCode"
            :editable="editMode"
            :input-options="fieldOptions.operationCode"
            @input="clearFieldValidationError('operationCode')"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        order="7"
      >
        <b-form-group>
          <label>{{ $t('asset-information.asset-info.operative-position')}}</label>
          <s-input-label-edit
            class="text-truncate"
            v-model.trim="assetInfo.operativePosition"
            :editable="editMode"
            :input-options="fieldOptions.operativePosition"
            @input="clearFieldValidationError('operativePosition')"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        order="8"
      >
        <b-form-group>
          <label>{{ $t('asset-information.asset-info.manufacturer')}}</label>
          <s-input-label-edit
            class="text-truncate"
            v-model.trim="assetInfo.manufacturer"
            :editable="editMode"
            :input-options="fieldOptions.modelAndManufacturer"
            @input="clearFieldValidationError('manufacturer')"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        order="9"
      >
        <b-form-group>
          <label>{{ $t('asset-information.asset-info.model')}}</label>
          <s-input-label-edit
            class="text-truncate"
            v-model.trim="assetInfo.model"
            :editable="editMode"
            :input-options="fieldOptions.modelAndManufacturer"
            @input="clearFieldValidationError('model')"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        lg="6"
        order="10"
      >
        <b-form-group>
          <label>{{ $t('asset-information.asset-info.manufacturing-year')}}</label>
          <s-input-label-edit
            class="text-truncate"
            v-model="yearProxy"
            :editable="editMode"
          >
            <b-form-input
              v-model="yearProxy"
              v-mask="fieldOptions.year.mask"
              :state="fieldOptions.year.state"
              :disabled="fieldOptions.year.disabled"
              @blur.native="validateYearField"
              @input="clearFieldValidationError('year')"
            />
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        order="11"
      >
        <b-form-group>
          <label>{{ $t('fields.general.observations')}}</label>
          <s-input-label-edit
            v-model.trim="assetInfo.observation"
            maxlength="2000"
            input-type="textarea"
            :editable="editMode"
            :input-options="fieldOptions.observation"
            @input="clearFieldValidationError('observation')"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import { minLength, required, minValue } from 'vuelidate/lib/validators';
import { getApiFieldsByKey } from '@/domain/data/installation/assetInformation/registrationDataFields';
import {
  formatter as dateTimeFormatter,
  getYear,
  isValid,
} from '@/services/dateTime/datetimeService';
import {
  formats,
  timezones,
} from '@/constants/localizer';

export function isGreaterThanManufacturingYear() {
  return !this.isOperationEntryLessThanManufacturingYear;
}

export default {
  name: 'RegistrationDataForm',
  props: {
    isTransformer: {
      type: Boolean,
      default: false,
    },
    validationErrors: {
      type: Array,
      default: () => ([]),
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    assetInfo: {
      type: Object,
      default: () => ({
        identification: null,
        operationEntry: null,
        description: null,
        serialNumber: null,
        installationCode: null,
        operationCode: null,
        operativePosition: null,
        manufacturer: null,
        model: null,
        year: null,
        observation: null,
      }),
    },
  },
  data: () => ({
    timeZoneUTC: timezones.UTC,
  }),
  created() {
    this.assetInfo.operationEntry = this.dateModelEdit;
    this.updateFormStatus(this.invalidForm);
  },
  computed: {
    invalidForm() {
      return this.$v.$invalid;
    },
    operationEntryState() {
      return this.$bsStateValidator(this.$v.assetInfo.operationEntry.$invalid);
    },
    yearState() {
      return this.$bsStateValidator(this.$v.assetInfo.year.$invalid);
    },
    identificationState() {
      return this.$bsStateValidator(this.$v.assetInfo.identification.$invalid);
    },
    serialNumberState() {
      return this.$bsStateValidator(this.$v.assetInfo.serialNumber.$invalid);
    },
    fieldOptions() {
      return {
        identification: {
          maxlength: 30,
          state: this.identificationState,
          disabled: this.saving,
        },
        serialNumber: {
          formatter: this.validCharactersRn5,
          maxlength: 50,
          state: this.serialNumberState,
          disabled: this.saving,
        },
        modelAndManufacturer: {
          maxlength: 200,
          disabled: this.saving,
        },
        year: {
          mask: ['####'],
          disabled: this.saving,
          state: this.yearState,
        },
        description: {
          maxlength: 200,
          disabled: this.saving,
        },
        observation: {
          disabled: this.saving,
          maxLength: 2000,
        },
        operativePosition: {
          disabled: this.saving,
          maxlength: 80,
        },
        operationCode: {
          disabled: this.saving,
          maxlength: 80,
        },
        installationCode: {
          disabled: this.saving,
          maxlength: 200,
        },
      };
    },
    dateMask() {
      return formats.DATE_SHORT;
    },
    dateModelRead() {
      const { dateMask, dateModelEdit } = this;
      return dateTimeFormatter(dateModelEdit, dateMask, this.timeZoneUTC);
    },
    isOperationEntryLessThanManufacturingYear() {
      const opts = { timeZone: this.timeZoneUTC };
      const entryYear = getYear(this.assetInfo.operationEntry, opts);
      const manufactureYear = this.assetInfo.year;
      return entryYear < manufactureYear;
    },
    dateModelEdit: {
      get() {
        const validDate = isValid(this.assetInfo.operationEntry);
        return validDate
          ? this.assetInfo.operationEntry
          : null;
      },
      set(value) {
        this.assetInfo.operationEntry = value;
      },
    },
    yearProxy: {
      get() {
        return this.assetInfo.year;
      },
      set(value) {
        this.$set(this.assetInfo, 'year', value !== '' ? parseInt(value, 10) : null);
      },
    },
  },
  methods: {
    clearFieldValidationError(field) {
      const apiFields = Object.keys(getApiFieldsByKey(field));
      const validationErrors = this.validationErrors.filter(
        error => !this.errorHasApiField(error, apiFields),
      );
      this.$emit('update:validation-errors', validationErrors);
    },
    errorHasApiField(error = {}, apiFields = []) {
      if (!error.field) {
        return Object.values(error.parameters).some(parameter => apiFields.includes(parameter));
      }
      const splitFields = error.field.split('.');
      const errorField = splitFields[splitFields.length - 1];
      return apiFields.includes(errorField);
    },
    isValidFromAPI(field) {
      if (!this.validationErrors) return true;
      const apiFields = Object.keys(getApiFieldsByKey(field));
      return !this.validationErrors.some(error => this.errorHasApiField(error, apiFields));
    },
    updateFormStatus(status) {
      this.$emit('form-status-update', status);
    },
    minCharValidate(model, charCount) {
      return model.length >= charCount;
    },
    validateEntryDate() {
      if (this.isOperationEntryLessThanManufacturingYear) {
        this.$pushAlert({
          code: 'MA060',
          messageParams: {
            field1: this.$t('asset-information.asset-info.operation-entry'),
            field2: this.$t('asset-information.asset-info.manufacturing-year'),
            operator: this.$t('general.less-than'),
          },
        });
      }
    },
    validateYearField() {
      this.validateEntryDate();
      if (this.assetInfo.year == null) {
        return;
      }
      if (!this.$v.assetInfo.year.minLength || !this.$v.assetInfo.year.minValue) {
        this.$pushAlert({
          code: 'MA010',
          messageParams: {
            NomeDoCampo: this.$t('asset-information.asset-info.manufacturing-year'),
            NroCaracteres: 4,
          },
        });
      }
    },
    validCharactersRn5(value) {
      return value && value.replace(/([^\w\u00C0-\u017FZ\-:.\\/]+)/g, '');
    },
  },
  watch: {
    invalidForm(value) {
      this.updateFormStatus(value);
    },
  },
  validations: {
    assetInfo: {
      identification: {
        required,
        validAPI() {
          return this.isValidFromAPI('identification');
        },
      },
      serialNumber: {
        required,
        validAPI() {
          return this.isValidFromAPI('serialNumber');
        },
      },
      year: {
        minLength: minLength(4),
        minValue: minValue(0),
        validAPI() {
          return this.isValidFromAPI('year');
        },
      },
      operationEntry: {
        isGreaterThanManufacturingYear,
        validAPI() {
          return this.isValidFromAPI('operationEntry');
        },
      },
    },
  },
};
</script>
<style lang="sass" scoped>
  @import "src/styles/defaults/mobile-first"
  .registration-body
    &__entry-input
      +media-min-lg
        display: flex
</style>
